import { Cushion, Grid, MaxWidth, Space } from '@pitchero/react-ui';
import { get, isEmpty, map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import RosBottomBanner from '../../adverts/ros-bottom-banner';
import TopBannerVideoPlayerSwitch from '../../adverts/top-banner-video-player-switch';
import NativeShelf from '../../native-adverts/shelf';
import SeasonFilter from '../season-filter';
import CLUB_PROP_TYPES from '../../../lib/prop-types/club';
import { SEASONS_PROP_TYPES } from '../../../lib/prop-types/season';
import {
  FIXTURE_STATISTICS_BREAKDOWN,
  FIXTURE_SUMMARY_STATISTICS,
  KEY_PLAYER_STATISTICS,
  PLAYER_STATISTICS_TABLES,
} from '../../../lib/prop-types/statistics';
import useBreakpoint from '../../../lib/use-breakpoint';
import MobileSubHeader from '../mobile-sub-header';
import FixturePointStatistics from './fixture-point-statistics';
import FixtureStatistics from './fixture-statistics';
import Statistics from './key-player-statistics/index';
import StatsLoadingState from './loading-state/index';
import NoPlayerStatsPlaceholder from './no-player-stats-placeholder';
import NoSeasonsPlaceholder from './no-seasons-placeholder';
import NoStatsPlaceholder from './no-stats-placeholder';
import PlayerStatisticsTable from './player-statistics-table/index';

const TeamStatistics = ({
  club,
  isLoading,
  clubPrimaryColour,
  sport,
  teamId,
  seasons,
  seasonId,
  onSeasonFilterChange,
  fixtureStatistics,
  pointStatistics,
  playerStatistics,
  playerStatisticsTables,
}) => {
  if (seasons.length === 0) {
    return <NoSeasonsPlaceholder />;
  }
  const renderKeyPlayerStatistics = () => {
    if (isEmpty(playerStatistics)) {
      return <NoPlayerStatsPlaceholder />;
    }

    const lapCols = playerStatistics.length >= 3 ? 3 : playerStatistics.length;
    const deskCols = playerStatistics.length >= 4 ? 4 : playerStatistics.length;

    return (
      <Space vertical="medium" responsive={[{ minWidth: 'palm', props: { vertical: 'large' } }]}>
        <Grid
          className="ie-grid-three-col"
          columns="1fr"
          columnGap="medium"
          rowGap="medium"
          responsive={[
            {
              minWidth: 'tab',
              props: {
                columns:
                  playerStatistics.length > 1 ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)',
                columnGap: 'large',
                rowGap: 'large',
              },
            },
            {
              minWidth: 'lap',
              props: {
                columns: `repeat(${lapCols}, minmax(0, 1fr))`,
                columnGap: 'large',
                rowGap: 'large',
              },
            },
            {
              minWidth: 'desk',
              props: {
                columns: `repeat(${deskCols}, minmax(0, 1fr))`,
                columnGap: 'large',
                rowGap: 'large',
              },
            },
          ]}
        >
          {map(playerStatistics, (statisticsGroup) => (
            <Statistics
              clubPrimaryColour={clubPrimaryColour}
              teamId={teamId}
              {...statisticsGroup}
              seasonId={seasonId}
              key={statisticsGroup.key}
            />
          ))}
        </Grid>
      </Space>
    );
  };

  const renderPlayerStatisticsTables = () => {
    if (isEmpty(playerStatisticsTables)) {
      return null;
    }

    return (
      <>
        <Cushion bottom="large">
          <RosBottomBanner />
        </Cushion>
        <Space vertical="medium" responsive={[{ minWidth: 'palm', props: { vertical: 'medium' } }]}>
          {map(playerStatisticsTables, (playerStatisticsTable, i) => (
            <PlayerStatisticsTable
              {...playerStatisticsTable}
              key={i}
              teamId={teamId}
              seasonId={seasonId}
            />
          ))}
        </Space>
      </>
    );
  };

  const renderResults = () => {
    if (get(fixtureStatistics, 'played', 0) === 0) {
      return (
        <>
          <SeasonFilter seasons={seasons} selected={seasonId} onChange={onSeasonFilterChange} />
          <NoStatsPlaceholder />
          <Cushion vertical="small">
            <RosBottomBanner />
          </Cushion>
        </>
      );
    }

    return (
      <>
        <SeasonFilter seasons={seasons} selected={seasonId} onChange={onSeasonFilterChange} />
        <Space vertical="medium" responsive={[{ minWidth: 'palm', props: { vertical: 'large' } }]}>
          <Grid
            className="ie-grid-two-col"
            columns="1fr"
            columnGap="medium"
            rowGap="medium"
            responsive={[
              {
                minWidth: 'tab',
                props: { columns: '1fr 1fr', columnGap: 'large', rowGap: 'large' },
              },
            ]}
          >
            {fixtureStatistics && <FixtureStatistics {...fixtureStatistics} includePlayedCount />}
            {pointStatistics && (
              <FixturePointStatistics
                sport={sport}
                totalPoints={pointStatistics.total_points}
                averagePointsFor={pointStatistics.average_points}
                averagePointsAgainst={pointStatistics.average_opposition_points}
              />
            )}
          </Grid>
        </Space>
        {renderKeyPlayerStatistics()}
        {renderPlayerStatisticsTables()}
        <NativeShelf />
      </>
    );
  };

  const { advertisingTier, sportId } = club;
  const isMobile = useBreakpoint();

  return (
    <MaxWidth component="div" maxWidth={1366} center>
      {isMobile && <MobileSubHeader title="Stats" transKey="teams:navigation_statistics" />}
      <Cushion bottom="medium">
        <TopBannerVideoPlayerSwitch
          advertisingTier={advertisingTier}
          pageType="team.statistics"
          sportId={sportId}
        />
      </Cushion>
      {isLoading && <StatsLoadingState />}
      {!isLoading && renderResults()}
    </MaxWidth>
  );
};

TeamStatistics.defaultProps = {
  isLoading: true,
  seasonId: null,
  fixtureStatistics: null,
  pointStatistics: null,
  playerStatistics: null,
  playerStatisticsTables: null,
};

TeamStatistics.propTypes = {
  club: CLUB_PROP_TYPES.isRequired,
  isLoading: PropTypes.bool,
  clubPrimaryColour: PropTypes.string.isRequired,
  teamId: PropTypes.number.isRequired,
  seasons: SEASONS_PROP_TYPES.isRequired,
  seasonId: PropTypes.number,
  onSeasonFilterChange: PropTypes.func.isRequired,
  fixtureStatistics: FIXTURE_SUMMARY_STATISTICS,
  pointStatistics: FIXTURE_STATISTICS_BREAKDOWN,
  sport: PropTypes.string.isRequired,
  playerStatistics: KEY_PLAYER_STATISTICS,
  playerStatisticsTables: PLAYER_STATISTICS_TABLES,
};

export default TeamStatistics;
